'use client';

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from 'react';

interface IGlobalBannerContext {
  bannerRef: React.RefObject<HTMLElement | null>;
  isBannerInView: boolean;
  isBannerVisible: boolean;
  handleClose: () => void;
  setIsBannerVisible: (isBannerVisible: boolean) => void;
}

const GlobalBannerContext = createContext<IGlobalBannerContext | undefined>(
  undefined,
);

interface IGlobalBannerProviderProps {
  bannerKey: string;
  children: ReactNode;
}

export const GlobalBannerProvider = ({
  bannerKey,
  children,
}: IGlobalBannerProviderProps) => {
  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(false);
  const [isBannerInView, setIsBannerInView] = useState<boolean>(false);
  const bannerRef = useRef<HTMLElement | null>(null);

  // Setup banner visibility from session storage
  useEffect(() => {
    const bannerClosed = sessionStorage.getItem(bannerKey);
    setIsBannerVisible(!bannerClosed);
  }, [bannerKey]);

  // IntersectionObserver for visibility
  useEffect(() => {
    if (!bannerRef.current || !isBannerVisible) {
      setIsBannerInView(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isBannerVisible) {
          setIsBannerInView(entry.isIntersecting);
        }
      },
      { threshold: 0 },
    );

    observer.observe(bannerRef.current);
    return () => observer.disconnect();
  }, [isBannerVisible]);

  const handleClose = (): void => {
    setIsBannerVisible(false);
    setIsBannerInView(false);
    sessionStorage.setItem(bannerKey, 'true');
  };

  return (
    <GlobalBannerContext.Provider
      value={{
        isBannerVisible,
        handleClose,
        isBannerInView,
        bannerRef,
        setIsBannerVisible,
      }}
    >
      {children}
    </GlobalBannerContext.Provider>
  );
};

export const useGlobalBanner = () => {
  const context = useContext(GlobalBannerContext);
  if (context === undefined) {
    throw new Error(
      'useGlobalBanner must be used within a GlobalBannerProvider',
    );
  }
  return context;
};
